import {
	convertFromAD,
	convertFromBS,
	format,
	parse,
	getDaysInBSMonth,
	START_YEAR,
	END_YEAR,
	type ADDate,
	type BSDate,
	type BSDateInput,
	type ConvertResult,
	type Language,
} from "./nibjar-nepali-helper";

// ─── Private method symbols ───────────────────────────────────────────────────

const convertToBSMethod = Symbol("convertFromAD()");
const convertToADMethod = Symbol("convertFromBS()");
const setAdBs = Symbol("setADBS()");
const setDayYearMonth = Symbol("setDayYearMonth()");

// ─── Class ────────────────────────────────────────────────────────────────────

export default class NibjarNepaliDate {
	private _jsDate!: Date;
	private _date!: number;
	private _year!: number;
	private _month!: number;
	private _day!: number;

	/**
	 * Construct a NibjarNepaliDate.
	 *
	 * Overloads:
	 * - `new NibjarNepaliDate()` — today
	 * - `new NibjarNepaliDate(timestamp: number)` — from Unix ms
	 * - `new NibjarNepaliDate(date: Date)` — from JS Date
	 * - `new NibjarNepaliDate(dateString: string)` — from "YYYY/MM/DD" or "DD/MM/YYYY"
	 * - `new NibjarNepaliDate(year, monthIndex, date)` — from BS year/month/date
	 */
	constructor();
	constructor(timestamp: number);
	constructor(date: Date);
	constructor(dateString: string);
	constructor(year: number, month: number, date: number);
	constructor(...args: [] | [number | string | Date] | [number, number, number]) {
		const constructorError = new Error("Invalid constructor arguments");

		if (args.length === 0) {
			// No arguments — use today
			this[convertToBSMethod](new Date());

		} else if (args.length === 1) {
			const argument = args[0];

			if (typeof argument === "number") {
				// Unix timestamp
				this[convertToBSMethod](new Date(argument));

			} else if (typeof argument === "string") {
				// Date string e.g. "2081/04/15"
				const { date, year, month } = parse(argument);
				this[setDayYearMonth](year, month, date);
				this[convertToADMethod]();

			} else if (argument instanceof Date) {
				// JS Date object
				this[convertToBSMethod](argument);

			} else {
				throw constructorError;
			}

		} else if (args.length === 3) {
			// BS year, month index, date
			const [year, month, date] = args as [number, number, number];
			this[setDayYearMonth](year, month, date);
			this[convertToADMethod]();

		} else {
			throw constructorError;
		}
	}

	// ─── Private symbol methods ───────────────────────────────────────────────

	[setDayYearMonth](year: number, month = 0, date = 1, day = 0): void {
		this._year = year;
		this._month = month;
		this._date = date;
		this._day = day;
	}

	[setAdBs](AD: ADDate, BS: BSDate): void {
		this[setDayYearMonth](BS.year, BS.month, BS.date, BS.day);
		this._jsDate = new Date(AD.year, AD.month, AD.date);
	}

	[convertToBSMethod](date: Date): void {
		const { AD, BS }: ConvertResult = convertFromAD(date);
		this[setAdBs](AD, BS);
	}

	[convertToADMethod](): void {
		const { AD, BS }: ConvertResult = convertFromBS({
			year: this._year,
			month: this._month,
			date: this._date,
		});
		this[setAdBs](AD, BS);
	}

	// ─── Public getters ───────────────────────────────────────────────────────

	/** Returns the underlying JavaScript Date (AD). */
	toJsDate(): Date {
		return this._jsDate;
	}

	/** Day of month in BS (1–32). */
	getDate(): number {
		return this._date;
	}

	/** BS year (e.g. 2081). */
	getYear(): number {
		return this._year;
	}

	/**
	 * BS month index (0 = Baisakh … 11 = Chaitra).
	 *
	 * Baisakh=0 Jestha=1 Asar=2 Shrawan=3 Bhadra=4 Aswin=5
	 * Kartik=6 Mangsir=7 Poush=8 Magh=9 Falgun=10 Chaitra=11
	 */
	getMonth(): number {
		return this._month;
	}

	/** Week day index (0 = Sunday … 6 = Saturday). */
	getDay(): number {
		return this._day;
	}

	// ─── Date object accessors ────────────────────────────────────────────────

	/**
	 * Returns both AD and BS as a single object.
	 *
	 * ```ts
	 * { BS: { year, month, date, day }, AD: { year, month, date, day } }
	 * ```
	 */
	getDateObject(): ConvertResult {
		return { BS: this.getBS(), AD: this.getAD() };
	}

	/** Returns BS fields as an object. */
	getBS(): BSDate {
		return {
			year: this._year,
			month: this._month,
			date: this._date,
			day: this._day,
		};
	}

	/** Returns AD fields as an object. */
	getAD(): ADDate {
		return {
			year: this._jsDate.getFullYear(),
			month: this._jsDate.getMonth(),
			date: this._jsDate.getDate(),
			day: this._jsDate.getDay(),
		};
	}

	// ─── Mutators ─────────────────────────────────────────────────────────────

	/**
	 * Set day of month. Positive or negative; overflows adjust month/year.
	 * @example a.setDate(-1) // moves to last day of previous month
	 */
	setDate(date: number): void {
		const oldDate = this._date;
		try {
			this._date = date;
			this[convertToADMethod]();
		} catch (e) {
			this._date = oldDate;
			throw e;
		}
	}

	/**
	 * Set month index. Positive or negative; overflows adjust year.
	 * @example a.setMonth(-1) // moves to month 11 of previous year
	 */
	setMonth(month: number): void {
		const oldMonth = this._month;
		try {
			this._month = month;
			this[convertToADMethod]();
		} catch (e) {
			this._month = oldMonth;
			throw e;
		}
	}

	/**
	 * Set BS year (positive integer only).
	 */
	setYear(year: number): void {
		const oldYear = this._year;
		try {
			this._year = year;
			this[convertToADMethod]();
		} catch (e) {
			this._year = oldYear;
			throw e;
		}
	}

	// ─── Formatting ───────────────────────────────────────────────────────────

	/**
	 * Format the BS date using a format string.
	 *
	 * ```
	 * YYYY YYYY YY | M MM MMM MMMM | D DD | d dd ddd
	 * ```
	 *
	 * @param formatString  Format string (e.g. "DD MMM, YYYY")
	 * @param language      "en" (default) | "np"
	 *
	 * @example
	 * new NibjarNepaliDate(2081, 3, 15).format("DD MMM, YYYY")       // "15 Shr, 2081"
	 * new NibjarNepaliDate(2081, 3, 15).format("DD MMMM, YYYY", "np") // "१५ श्रावण, २०८१"
	 */
	format(formatString: string, language?: Language): string {
		return format(this.getBS(), formatString, language);
	}

	toString(): string {
		return this.format("ddd DD, MMMM YYYY");
	}
}

// Re-export shared types and utilities so consumers only need to import from this file
export { getDaysInBSMonth, START_YEAR, END_YEAR };
export type { ADDate, BSDate, BSDateInput, ConvertResult, Language };
